import { Component, OnInit } from '@angular/core';
import { FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { QuoteService } from '../../services/quotes.service';
import { ActivatedRoute, Router } from '@angular/router';
import { RequestUpdateQuickQuote, RequestUpdateQuote } from '../../types/dai-server/quotes/quote-requests';
import { ControlsOf, ControlsOfExcept } from '../../types/ControlsOf';
import { firstValueFrom } from 'rxjs';
import { FormGroupComponent } from "../product-editor/form-group.component";
import { CurrencyInputComponent } from '../product-editor/currency-input.component';
import { LoadingComponent } from "../../../../node_modules/@digi-america/shared/src/lib/components/loading/loading.component";
import { NgbDatepickerState, NgbDateStruct, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-quote-update',
  imports: [ReactiveFormsModule, FormGroupComponent, CurrencyInputComponent, LoadingComponent, NgbInputDatepicker],
  templateUrl: './quote-update.component.html',
  styleUrl: './quote-update.component.scss',
  standalone: true
})
export class QuoteUpdateComponent implements OnInit {
  public form?: FormGroup<{ quickQuote: FormGroup<ControlsOf<RequestUpdateQuickQuote>>; quote: FormGroup }>;
  public quoteId: string = "";
  public isQuickQuote: boolean = false;
  public hasHiddenMarkup: boolean = false;
  public todayDate: Date = new Date();

  constructor(
    private quoteService: QuoteService,
    private fb: NonNullableFormBuilder,
    private route: ActivatedRoute,
    private router: Router) { }


  ngOnInit(): void {
    this.route.paramMap.subscribe(async (params) => {
      this.quoteId = params.get("quoteId") ?? "";
      const quote = await firstValueFrom(this.quoteService.getQuote(this.quoteId));
      this.isQuickQuote = quote.isQuickQuote;
      this.hasHiddenMarkup = !!quote.hiddenMarkup;

      var materialETA = quote.materialETA ? new Date(quote.materialETA) : null;
      var materialETADateModel: NgbDateStruct | null = null;
      if (materialETA) {
        materialETADateModel = {
          year: materialETA.getFullYear(),
          month: materialETA.getMonth() + 1,
          day: materialETA.getDate()
        };
      }

      const newForm = this.fb.group({
        quote: this.fb.group({
          salesTaxPercent: this.fb.control(quote.salesTaxPercent, [Validators.required, Validators.min(0)]),
          // Read only, shown for reference when the quote already has one
          hiddenMarkup: this.fb.control({ value: quote.hiddenMarkup ?? 0, disabled: true }),
          shipping: this.fb.control(quote.shipping, [Validators.min(0), Validators.required]),
          rollupShipping: this.fb.control(quote.rollupShipping),
          shippingSalesTax: this.fb.control(quote.shippingSalesTax),
          discount: this.fb.control(quote.discount, [Validators.min(0), Validators.required]),
          discountReason: this.fb.control(quote.discountReason, [Validators.maxLength(200)]),
          materialETA: this.fb.control(materialETADateModel),
          fob: this.fb.control(quote.fob, Validators.maxLength(50))
        }),
        quickQuote: this.fb.group({
          customer: this.fb.group({
            name: this.fb.control(quote.customer.name, [Validators.required, Validators.maxLength(50)]),
            addressLine1: this.fb.control(quote.customer.addressLine1, Validators.maxLength(50)),
            addressLine2: this.fb.control(quote.customer.addressLine2, Validators.maxLength(50)),
            city: this.fb.control(quote.customer.city, Validators.maxLength(50)),
            state: this.fb.control(quote.customer.state, Validators.maxLength(50)),
            zip: this.fb.control(quote.customer.zip, Validators.maxLength(50)),
            phone: this.fb.control(quote.customer.phone, Validators.maxLength(50)),
            email: this.fb.control(quote.customer.email, [Validators.maxLength(50), Validators.email]),
            pointOfContactName: this.fb.control(quote.customer.pointOfContactName ?? "", Validators.maxLength(50)),
            pointOfContactTitle: this.fb.control(quote.customer.pointOfContactTitle ?? "", Validators.maxLength(50)),
          }),
          useBillingForShipping: this.fb.control(quote.useBillingForShipping),
          shippingAddress: this.fb.group({
            name: this.fb.control(quote.shippingAddress.name, Validators.maxLength(50)),
            addressLine1: this.fb.control(quote.shippingAddress.addressLine1, Validators.maxLength(50)),
            addressLine2: this.fb.control(quote.shippingAddress.addressLine2, Validators.maxLength(50)),
            city: this.fb.control(quote.shippingAddress.city, Validators.maxLength(50)),
            state: this.fb.control(quote.shippingAddress.state, Validators.maxLength(50)),
            zip: this.fb.control(quote.shippingAddress.zip, Validators.maxLength(50)),
          }),
          salesRepresentative: this.fb.group({
            name: this.fb.control(quote.salesRepresentative.name, Validators.maxLength(50)),
            title: this.fb.control(quote.salesRepresentative.title, Validators.maxLength(50)),
            phoneNumber: this.fb.control(quote.salesRepresentative.phoneNumber, Validators.maxLength(50)),
            email: this.fb.control(quote.salesRepresentative.email, [Validators.maxLength(50), Validators.email]),
          }),
        })
      });

      this.form = newForm;
    });
  }

  public async submit() {
    if (this.form) {
      if (this.isQuickQuote) {
        const update: RequestUpdateQuickQuote = this.form.controls.quickQuote.getRawValue();
        await firstValueFrom(this.quoteService.updateQuickQuote(this.quoteId, update));
      }

      // Disabled fields are left out, so the hidden markup is never changed from here
      const update = this.form.controls.quote.value;
      var eta = update.materialETA;
      update.materialETA = (eta ? new Date(eta.year, eta.month - 1, eta.day) : null)?.toISOString();
      await firstValueFrom(this.quoteService.updateQuote(this.quoteId, update));
    }

    // Navigate back to the quote page
    this.router.navigate(["/", "quote-view", this.quoteId]);
  }

}
